import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { authApi } from '@/lib/auth_api';
import { toast } from 'sonner';
import i18n, { LANGUAGE_STORAGE_KEY, LANGUAGE_EXPLICIT_KEY } from '@/i18n/config';
import { useLocation } from 'react-router-dom';
import { skipsLoomAuthProbe, usesOptionalLoomAuthProbe } from '@/lib/publicRoutes';

const PROFILE_API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Sep-2026 fix — a previous version of this helper unconditionally pulled
// `me.language` from the server into i18n on EVERY /auth/me resolution
// (route nav, 60s poll, tab focus). That meant: pick Chinese on the landing
// page as a guest → sign in → the very next /auth/me (fired on that same
// navigation) overwrote i18n back to the account's stale stored default
// (usually 'en'), so the whole rest of the app reset to English the moment
// you entered a logged-in surface. The fix: once a language has been
// EXPLICITLY picked in this browser (`pw_lang_explicit`), that choice is
// authoritative locally — we push it UP to the profile instead of letting
// a stale profile value overwrite it. We only ever pull server -> local
// the very first time a browser has no explicit pick yet (true
// cross-device restore for a brand-new browser/device).
// Oct-2026 — avoids re-firing the same PUT /auth/me/language call on
// every 60s poll / tab-focus while we're waiting for the server to catch
// up to a local explicit choice (testing_agent review note).
let _lastPushedLanguage = null;

// Feb-2026 — Mirror the writer's chapter-finish celebration style into
// localStorage once per login (`celebrate.js` reads it synchronously, with
// no API access, so it can fire mid chapter-generation without a round
// trip). Fetched once per distinct user_id — the 60s auth-poll/focus
// re-checks shouldn't re-fetch the profile every time. Cleared on logout
// so a shared-browser's next sign-in never inherits a stale style.
let _celebSyncedForUser = null;
async function _syncCelebrationStyle(me) {
  if (!me?.user_id || _celebSyncedForUser === me.user_id) return;
  try {
    const { data } = await axios.get(`${PROFILE_API}/profile/me`, { withCredentials: true });
    window.localStorage.setItem('pw_celebration_style', data?.celebration_style || 'confetti');
    // Oct-2026 — also sync the appearance/theme so it survives cross-device logins
    if (data?.theme_mode) {
      window.dispatchEvent(new CustomEvent('pw:theme-synced', { detail: data.theme_mode }));
    }
    _celebSyncedForUser = me.user_id;   // only mark done once the fetch actually succeeded
  } catch { /* best-effort — celebrate.js already defaults to 'confetti'; retried on next checkAuth */ }
}

// 2026-10 — The Admin Loom bridge only auto-fires inside the actual
// writer workspace, never on public/marketing/admin pages.
function isLoomSurface(pathname = '') {
  return pathname.startsWith('/app') || pathname.startsWith('/loom');
}

function _syncLanguageFromUser(me) {
  const serverCode = (me?.language || '').slice(0, 2).toLowerCase();
  const current = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2).toLowerCase();
  let explicit = false;
  try { explicit = window.localStorage.getItem(LANGUAGE_EXPLICIT_KEY) === '1'; } catch { /* ignore */ }

  if (explicit) {
    // Local choice wins. If the profile hasn't caught up yet, push it —
    // but never pull the stale server value back into the live UI.
    if (serverCode && serverCode !== current && _lastPushedLanguage !== current) {
      _lastPushedLanguage = current;
      authApi.setLanguage(current).catch(() => { _lastPushedLanguage = null; });
    }
    return;
  }

  if (!serverCode || serverCode === current) return;
  // No explicit local pick yet — safe to restore the server-saved
  // preference (e.g. first load on a brand-new device).
  i18n.changeLanguage(serverCode).catch(() => { /* ignore */ });
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, serverCode);
    window.localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');
  } catch { /* private-mode / storage blocked */ }
}

const Ctx = createContext(null);

// Tier label map — used for the tier-change toast notification
const _TIER_LABELS = {
  free:         'Free',
  basic:        'Basic',
  author:       'Author',
  pro_plus:     'Pro Plus',
  publisher:    'Publisher',
  studio:       'Studio',
  studio_byok:  'Studio (BYOK)',
  studio_pack:  'Studio Pack',
  author_pack:  'Author Pack',
  premier:      'Premier',
};
function _formatTierLabel(tier) {
  if (!tier) return 'Free';
  return _TIER_LABELS[tier] ||
    tier.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// iter-p2-q — QA training-user reset. When `me.is_qa_training_user`
// flips true (i.e. `admin-sandbox@invalid.crownnrose.local` just signed in), wipe every
// guided-tour / lesson-state / trial-upgrade / prompt-mode marker
// out of localStorage & sessionStorage exactly ONCE per pod-login so
// the QA user walks through every training popup every session.
function _wipeTrainingMarkersOncePerLogin(userId) {
  if (!userId) return;
  try {
    const flagKey = `pw:qa-reset:${userId}`;
    // Reset every fresh browser session — sessionStorage is the right
    // scope because a new tab is a new "login" for QA purposes.
    if (window.sessionStorage.getItem(flagKey) === '1') return;
    const wipeExact = [
      'pw:tour-completed-v1',
      'pw_training_lesson_state_v1',
      'pw_disable_next_lesson_toast',
      'pw_prompt_mode',
      'biz_ping_muted',
    ];
    wipeExact.forEach((k) => { try { window.localStorage.removeItem(k); } catch { /* ignore */ } });
    // Every keyed tour/coach/dialog marker.
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i) || '';
      if (k.startsWith('pw:tour-') || k.startsWith('pw:coach-')
          || k.startsWith('pw:welcome-') || k.startsWith('pw:helptip-')) {
        try { window.localStorage.removeItem(k); } catch { /* ignore */ }
      }
    }
    // Also nuke session-scoped once-per-session markers.
    for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = window.sessionStorage.key(i) || '';
      if (k.startsWith('pw:') || k.startsWith('pw_')) {
        try { window.sessionStorage.removeItem(k); } catch { /* ignore */ }
      }
    }
    window.sessionStorage.setItem(flagKey, '1');
    // eslint-disable-next-line no-console
    console.info('[qa-user] Training markers wiped — every popup will fire again.');
  } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  // Track the user's tier across polls — used to detect admin-side tier changes
  // and surface a toast so users don't wonder why their capabilities changed.
  const prevPlanRef = useRef(null);
  // Sep-2026 flash fix — only show the loading splash on the very first
  // auth probe. Subsequent re-checks on browser back/forward are silent
  // (no setChecking(true)) so navigating the history stack never flashes
  // the CheckingSplash screen again.
  const initialCheckDoneRef = useRef(false);

  const checkAuth = useCallback(async (pathname) => {
    try {
      const me = await authApi.me();
      // ── Tier-change notification ──────────────────────────────────────────
      // prevPlanRef.current is null on the very first call (initial load /
      // login) — skip the toast then. On subsequent 15s polls, if the admin
      // has upgraded / downgraded the user's plan, show a brief success toast
      // so the user knows their capabilities updated without a page refresh.
      const newTier = me?.subscription_tier ?? null;
      if (prevPlanRef.current !== null && prevPlanRef.current !== newTier) {
        toast.success(`Plan updated to ${_formatTierLabel(newTier)}`, {
          description: 'Your new capabilities are now active.',
          duration: 6000,
        });
      }
      prevPlanRef.current = newTier;
      // ─────────────────────────────────────────────────────────────────────
      setUser(me);
      _syncLanguageFromUser(me);
      _syncCelebrationStyle(me);
      if (me?.is_qa_training_user) _wipeTrainingMarkersOncePerLogin(me.user_id);
    } catch (err) {
      // 2026-10 — Admin Loom bridge. A signed-in admin (has an
      // `admin_session` cookie, but no writer `session_token`) who
      // lands on `/app` is automatically dropped into the permanent
      // "Admin Troubleshooting" sandbox writer account — no separate
      // writer login required. `adminLoomBridge()` 401s silently for
      // any non-admin caller, so this is a no-op for ordinary signed-
      // out visitors.
      if (err?.response?.status === 401 && isLoomSurface(pathname)) {
        try {
          await authApi.adminLoomBridge();
          const me = await authApi.me();
          setUser(me);
          _syncLanguageFromUser(me);
          _syncCelebrationStyle(me);
          setChecking(false);
          return;
        } catch { /* not an admin, or bridge failed — fall through below */ }
      }
      // ── Only clear the session on a CONFIRMED 401 (truly unauthenticated). ──
      // Network errors (no response), 5xx server errors, and CDN/gateway
      // errors are all transient — preserving the current user avoids
      // kicking the writer out of a 3-hour session because the backend
      // hiccuped for one 15-second polling cycle.  The writer sees nothing;
      // the next successful poll restores full capability silently.
      const httpStatus = err?.response?.status;
      if (httpStatus === 401) {
        setUser(null);
      } else if (httpStatus) {
        // 5xx or unexpected HTTP error — log but do not clear the session.
        console.error('[auth] /me check failed (status', httpStatus, ') — keeping session');
      } else {
        // Network error (no HTTP response at all) — keep session.
        console.warn('[auth] /me network error — keeping session:', err?.message || err);
      }
    } finally {
      setChecking(false);
    }
  }, []);

  const checkOptionalAuth = useCallback(async () => {
    try {
      const response = await authApi.optionalMe();
      const me = response?.user || null;
      setUser(me);
      if (me) {
        _syncLanguageFromUser(me);
        _syncCelebrationStyle(me);
        if (me.is_qa_training_user) _wipeTrainingMarkersOncePerLogin(me.user_id);
      }
    } catch (err) {
      if (err?.response?.status) console.error('[auth] optional session check failed', err);
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setChecking(false);
      return;
    }
    if (skipsLoomAuthProbe(location.pathname)) {
      setChecking(false);
      return;
    }
    if (!initialCheckDoneRef.current) setChecking(true);
    initialCheckDoneRef.current = true;
    if (usesOptionalLoomAuthProbe(location.pathname)) checkOptionalAuth();
    else checkAuth(location.pathname);
  }, [checkAuth, checkOptionalAuth, location.pathname]);

  // Auto-refresh `/auth/me` when the tab regains focus or every 15s so
  // role / permission changes made by an admin are picked up without
  // requiring the user to log out and back in.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { checkAuth(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkAuth();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(checkAuth, 15_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [user, checkAuth]);

  /* iter-72i — Trial-expiry edge case. If a signed-in trial user is
   * mid-session when their `trial_expires_at` ticks past, we want the
   * TrialExpiredScreen to take over WITHOUT them having to hit a
   * mutation endpoint that 401s first. Schedule a one-shot refetch at
   * the exact expiry instant (clamped to 24 h so a far-future expiry
   * doesn't create a runaway timer). The 60-second focus/visibility
   * polling above covers everything else. */
  useEffect(() => {
    if (!user || !user.is_trial || user.trial_expired) return undefined;
    if (!user.trial_expires_at) return undefined;
    const expiresAt = new Date(user.trial_expires_at).getTime();
    if (Number.isNaN(expiresAt)) return undefined;
    const delay = Math.min(Math.max(expiresAt - Date.now(), 500), 24 * 3600 * 1000);
    const id = window.setTimeout(checkAuth, delay + 500);   // +500 ms safety
    return () => window.clearTimeout(id);
  }, [user, checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); }
    catch (err) { console.error('[auth] logout failed', err); }
    setUser(null);
    prevPlanRef.current = null;  // reset so next login doesn't fire a tier toast
    _celebSyncedForUser = null;
    // iter-72f — clear the trial-upgrade session marker so the next
    // sign-in re-triggers the dialog (spec: "each time the 14-day
    // user logs in, they should be offered the upgrade").
    // iter-72g — also clear the daily marker used while the trial is
    // urgent (≤ 3 days) so re-login mid-day re-triggers immediately.
    try {
      window.sessionStorage.removeItem('pw:trial-upgrade-shown');
      window.localStorage.removeItem('pw:trial-upgrade-shown-day');
      window.localStorage.removeItem('pw_celebration_style');
    } catch (_e) { /* private mode */ }
  }, []);

  const value = useMemo(
    () => ({ user, setUser, checking, login, logout, refresh: checkAuth }),
    [user, checking, login, logout, checkAuth]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
